import { useEffect, useState } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";
import { FormAlert } from "../../../Shared/Form";
import adminService from "../../../../services/admin.service";
import { statratRed } from "../../../../config/constants";
import LoadingOverlay from "../../../Shared/LoadingOverlay";
import { AdminUserPaymentHistory } from "../../../../types/adminPaymentHistory";
import { formatCurrency, formatDateTime } from "../../../../hooks/formatNumber";

interface Column {
    label: string;
    align: 'left' | 'center' | 'right';
}
const columns: Column[] = [
    { label: 'Date', align: 'left' },
    { label: 'Total', align: 'left' },
    { label: 'Status', align: 'left' },
    { label: 'Message', align: 'center' },
    { label: '', align: 'center' },
    { label: 'Receipt', align: 'center' },
    { label: 'Status', align: 'right' },
    { label: 'Total', align: 'right' },
    { label: 'Date', align: 'right' },
]

export const PaymentInfoCell = ({ uid, name }: { uid: number, name: string }) => {
    const [paymentData, setPaymentData] = useState<AdminUserPaymentHistory | null>(null);
    const [openPaymentInfo, setOpenPaymentInfo] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        async function fetchData() {
            setIsLoading(true);
            const response = await adminService.queryUserPaymentHistory(uid);
            setIsLoading(false);
            setPaymentData(response);
        }
        if (openPaymentInfo) {
            fetchData();
        }
    }, [openPaymentInfo, uid]);

    function formatDateTimeRow(datetime: number | undefined) {
        return datetime ? formatDateTime(datetime) : "-";
    }

    function formatCurrencyRow(num: number | undefined, currency: string | undefined) {
        return num ? formatCurrency(num / 100, currency, 'auto') : "-";
    }

    return (
        <>
            <TableCell
                component="th"
                scope="row"
                onClick={() => setOpenPaymentInfo(true)}
                sx={{ cursor: "pointer" }}
            >
                <Typography color={statratRed}>{name}</Typography>
            </TableCell>
            <Dialog
                open={openPaymentInfo}
                onClose={() => { setOpenPaymentInfo(false) }}
                fullWidth={true}
                maxWidth="xl"
                aria-labelledby="payment-info-title"
                aria-describedby="payment-info-description"
            >
                {isLoading && <LoadingOverlay open={isLoading} /> }
                <DialogTitle id="payment-info-title" sx={{ minWidth: 800 }}>
                    Payment Info - {name}
                </DialogTitle>
                <DialogContent sx={{ minWidth: 1200 }}>
                    <TableContainer component={Paper} sx={{ maxHeight: 440 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell align="center" colSpan={4}>
                                        Database Records
                                        {paymentData?.dbAmount && ` - ${formatCurrency(paymentData.dbAmount / 100)}`}
                                    </TableCell>
                                    <TableCell align="center" colSpan={1} />
                                    <TableCell align="center" colSpan={4}>
                                        Stripe Records
                                        {paymentData?.stripeAmount && ` - ${formatCurrency(paymentData.stripeAmount / 100)}`}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    {columns.map((column, index) => 
                                        <TableCell align={column.align} style={{ top: 57 }} key={index}>
                                            <Typography variant="body2">{column.label}</Typography>
                                        </TableCell>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {!paymentData || paymentData.records.length === 0 ? (
                                    <TableRow key={0} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                        <TableCell component="th" scope="row">
                                            <Typography>No payment records found</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paymentData?.records.map((record, index) =>
                                        <TableRow
                                            key={index}
                                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                        >
                                            <TableCell>
                                                <Typography>
                                                    {formatDateTimeRow(record.created)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography>
                                                    {formatCurrencyRow(record.amountTotal, "CAD")}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography textTransform='capitalize'>
                                                    {record.status ?? "-"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography>
                                                    {record.message ? (
                                                        <Button
                                                            onClick={() => setAlertMessage(record.message)}
                                                            style={{ padding: 0, backgroundColor: 'transparent' }}
                                                        >
                                                            View Message
                                                        </Button>
                                                    ) : (
                                                        "-"
                                                    )} 
                                                </Typography>
                                            </TableCell>

                                            <TableCell align="center" />
                                            
                                            <TableCell align="center">
                                                <Typography>
                                                    {record.stripeReceiptUrl ? (
                                                        <a href={record.stripeReceiptUrl} target="_blank" rel="noreferrer">
                                                            <Button style={{ padding: 0, backgroundColor: 'transparent' }}>
                                                                View Receipt
                                                            </Button>
                                                        </a>
                                                    ) : (
                                                        "-"
                                                    )} 
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography textTransform='capitalize'>
                                                    {record.stripeStatus ?? "-"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography>
                                                    {formatCurrencyRow(record.stripeAmountTotal, record.stripeCurrency)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography>
                                                    {formatDateTimeRow(record.stripeCreated)}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <FormAlert
                        label="campaign"
                        open={alertMessage.length > 0}
                        setOpen={(v) => { if (!v) { setAlertMessage("") } }}
                        description={alertMessage}
                        disabled={isLoading}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setOpenPaymentInfo(false) }}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
